import { PresentationPageKind } from '@/types/schema';

import {
  buildPageItems,
  BundlePageItem,
  BundlePageReorderList,
  BundlePageSubItem,
} from './BundlePageReorderList';

export type WaterfallConfigKey =
  | 'includeCoverPage'
  | 'includeAssumptionsPage'
  | 'includeSummaryPage'
  | 'includeDiagramPage'
  | 'includeTaxOverviewPage';

export type WaterfallSubItemConfigKey =
  | 'includeSummaryBars'
  | 'includeSummaryTiles'
  | 'showSummaryTileDetails'
  | 'includeOverview'
  | 'includeBeforeFirstDeath'
  | 'includeAfterFirstDeath'
  | 'includeAfterSecondDeath'
  | 'summaryHideZeroDollarItems';

/**
 * Shape accepted by buildWaterfallPageItems. Accepts any of the GraphQL
 * fragment types or local config types that carry waterfall flags — extra
 * fields like __typename are allowed and ignored.
 */
export type WaterfallPageConfig = {
  [K in WaterfallConfigKey | WaterfallSubItemConfigKey]?: boolean | null;
};

export interface WaterfallPageSubItemDefinition {
  configKey: WaterfallSubItemConfigKey;
  label: string;
  /** When set, disabled while the sibling sub-item with this configKey is unchecked. */
  dependsOn?: WaterfallSubItemConfigKey;
  /** True for a display option that does not gate page inclusion. */
  isDisplayOption?: boolean;
  /** Checked state when the config has no value. Defaults to true. */
  defaultChecked?: boolean;
}

export interface WaterfallPageSubItem extends BundlePageSubItem {
  configKey: WaterfallSubItemConfigKey;
  dependsOn?: WaterfallSubItemConfigKey;
}

export interface WaterfallPageItem extends BundlePageItem {
  configKey: WaterfallConfigKey | null;
  /** When true, the page cannot be unchecked. */
  alwaysIncluded?: boolean;
  /** Optional nested sub-items for this page (e.g. bars/tiles under Summary). */
  subItems?: WaterfallPageSubItem[];
}

export interface WaterfallPageDefinition {
  id: string;
  pageKind: PresentationPageKind;
  configKey: WaterfallConfigKey | null;
  label: string;
  alwaysIncluded?: boolean;
  subItemDefinitions?: WaterfallPageSubItemDefinition[];
}

/** Summary options shown while the updated summary page is off. */
const LEGACY_SUMMARY_SUB_ITEMS: WaterfallPageSubItemDefinition[] = [
  { configKey: 'includeSummaryBars', label: 'Include summary bars' },
  { configKey: 'includeSummaryTiles', label: 'Include summary tiles' },
  {
    configKey: 'showSummaryTileDetails',
    label: 'Include details on summary tiles',
    dependsOn: 'includeSummaryTiles',
  },
];

/** Summary options shown while the updated summary page is on (T1-6658). */
const UPDATED_SUMMARY_SUB_ITEMS: WaterfallPageSubItemDefinition[] = [
  { configKey: 'includeOverview', label: 'Overview' },
  { configKey: 'includeBeforeFirstDeath', label: 'Before 1st death' },
  { configKey: 'includeAfterFirstDeath', label: 'After 1st death' },
  { configKey: 'includeAfterSecondDeath', label: 'After 2nd death' },
  {
    configKey: 'summaryHideZeroDollarItems',
    label: 'Hide entities with $0 value',
    isDisplayOption: true,
    defaultChecked: false,
  },
];

export interface WaterfallPageDefinitionOptions {
  /** True when the updated summary page replaces the bars and tiles. */
  useUpdatedSummaryPage?: boolean;
  /**
   * False for a single-grantor household, which never reaches a second death.
   * Templates belong to no household, so they keep the option.
   */
  isTwoClientHousehold?: boolean;
}

/**
 * The 5 waterfall page types in their default order, with the Summary options
 * that suit the given context.
 */
export function getWaterfallPageDefinitions({
  useUpdatedSummaryPage = false,
  isTwoClientHousehold = true,
}: WaterfallPageDefinitionOptions = {}): WaterfallPageDefinition[] {
  if (!useUpdatedSummaryPage) return WATERFALL_PAGE_DEFINITIONS;

  const subItemDefinitions = UPDATED_SUMMARY_SUB_ITEMS.filter(
    ({ configKey }) =>
      isTwoClientHousehold || configKey !== 'includeAfterSecondDeath'
  );

  return WATERFALL_PAGE_DEFINITIONS.map((definition) =>
    definition.id === 'summary'
      ? { ...definition, subItemDefinitions }
      : definition
  );
}

/** The 5 waterfall page types in their default order. */
export const WATERFALL_PAGE_DEFINITIONS: WaterfallPageDefinition[] = [
  {
    id: 'cover',
    pageKind: PresentationPageKind.WaterfallCoverPage,
    configKey: 'includeCoverPage',
    label: 'Cover',
  },
  {
    id: 'assumptions',
    pageKind: PresentationPageKind.WaterfallAssumptionsPage,
    configKey: 'includeAssumptionsPage',
    label: 'Assumptions',
  },
  {
    id: 'summary',
    pageKind: PresentationPageKind.WaterfallSummaryPage,
    configKey: 'includeSummaryPage',
    label: 'Summary',
    subItemDefinitions: LEGACY_SUMMARY_SUB_ITEMS,
  },
  {
    id: 'diagram',
    pageKind: PresentationPageKind.WaterfallDiagramPage,
    configKey: 'includeDiagramPage',
    label: 'Diagram',
  },
  {
    id: 'taxOverview',
    pageKind: PresentationPageKind.WaterfallTaxOverviewPage,
    configKey: 'includeTaxOverviewPage',
    label: 'Tax overview',
  },
];

/**
 * Builds a WaterfallPageItem[] from a config record and a saved page order.
 * Shared between the template and presentation waterfall modals.
 *
 * @param config - Record mapping configKey to boolean (e.g. includeCoverPage: true).
 *                 Individual nullish values default to the sub-item's
 *                 `defaultChecked`, or to true (matching the GraphQL
 *                 normalization convention). If the entire config is null/undefined,
 *                 all pages default to unchecked.
 * @param savedPageKinds - Ordered list of PresentationPageKind from the bundle's
 *                         existing pages array, used to restore saved ordering.
 */
export function buildWaterfallPageItems(
  config: WaterfallPageConfig | null | undefined,
  savedPageKinds: PresentationPageKind[],
  pageDefinitions: WaterfallPageDefinition[] = WATERFALL_PAGE_DEFINITIONS
): WaterfallPageItem[] {
  const items = buildPageItems<WaterfallPageItem>(
    pageDefinitions,
    config,
    savedPageKinds
  );

  // Hydrate sub-items from definitions and config
  for (const item of items) {
    const def = pageDefinitions.find((d) => d.id === item.id);
    if (def?.subItemDefinitions) {
      item.subItems = def.subItemDefinitions.map((subDef) => ({
        configKey: subDef.configKey,
        label: subDef.label,
        dependsOn: subDef.dependsOn,
        isDisplayOption: subDef.isDisplayOption,
        checked: config?.[subDef.configKey] ?? subDef.defaultChecked ?? true,
      }));
    }
  }

  return items;
}

/**
 * Drag-and-drop reorderable list of waterfall pages with checkboxes.
 * Must be rendered inside a FormProvider whose shape includes `pages: WaterfallPageItem[]`.
 */
export const WaterfallPageReorderList = BundlePageReorderList;
